import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { platformMessages } from 'ml-components-platform-react';
import metadata from './AdditionalInfoToPurchasePagePolChange.metadata.json5';
import { AccountService } from 'gw-capability-gateway-policy';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';

import messages from './AdditionalInfoToPurchasePagePolChange.messages';
import styles from './AdditionalInfoToPurchasePagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const AdditionalInfoToPurchasePagePolChange = (props) => {
    const { wizardData, goNext, updateWizardData, currentStep } = props;
    const { submissionVM: endorsementVM } = wizardData;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation('PUPAdditionalInformationToPurchasePagePolChange');
    const { refreshData } = useDataRefresh();

    const [showErrors, setShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [hasDuplicateLicenseNumber, setHasDuplicateLicenseNumber] = useState(false);
    const [hidePage, setHidePage] = useState(true);
    const [inValidVin, setInValidVin] = useState([]);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService()

    const {
        clearBlockAlerts,
        addWarningAlert,
        addErrorAlert
    } = MLNotificationBlockUtil();

    const isExcludedDriver = (vehicleOperatorVM) => {
        return vehicleOperatorVM.driverType.value.code === 'excluded';
    };

    const isDriver = (vehicleOperatorVM) => {
        switch (vehicleOperatorVM.driverType.value.code) {
            case 'rated':
            case 'excluded':
                return true;
            default:
                return false;
        }
    };

    const isPrimaryNamedInsured = (vehicleOperatorVM) => vehicleOperatorVM.isPrimaryNamedInsured.value;

    const pupModel = endorsementVM.lobData.personalUmbrella;
    const vehiclesVM = useMemo(() => {
        return pupModel.coverables.vehicles.children;
    }, [pupModel]);

    const sortedVehicleOperatorsML = useMemo(() => {
        const getOperatorSortPriority = (vehicleOperatorVM) => {
            if (isPrimaryNamedInsured(vehicleOperatorVM)) {
                return 0;
            }
            switch (vehicleOperatorVM.driverType.value.code) {
                case 'rated':
                    return 1;
                case 'household':
                    return 2;
                case 'excluded':
                    return 3;
                default:
                    return 9;
            }
        };
        const getSortedOperatorsList = (vehicleOperatorsToSort) => {
        // Sorting by name first and then with Driver's priority to keep the order of the sorted array consistent.
            const nameSortedOperatorsList = vehicleOperatorsToSort.sort((vehicleOperator1VM, vehicleOperator2VM) => {
                const operatorName1 = vehicleOperator1VM.displayName.value ? vehicleOperator1VM.displayName.value.toUpperCase() : '';
                const operatorName2 = vehicleOperator2VM.displayName.value ? vehicleOperator2VM.displayName.value.toUpperCase() : '';
                if (operatorName1 < operatorName2) {
                    return -1;
                }

                if (operatorName1 > operatorName2) {
                    return 1;
                }

                return 0;
            });


            return nameSortedOperatorsList.sort((vehicleOperator1VM, vehicleOperator2VM) => {
                const operator1Priority = getOperatorSortPriority(vehicleOperator1VM);
                const operator2Priority = getOperatorSortPriority(vehicleOperator2VM);

                if (operator1Priority < operator2Priority) {
                    return -1;
                }

                if (operator1Priority > operator2Priority) {
                    return 1;
                }

                return 0;
            });
        };

        const operatorsToSort = [...pupModel.coverables.vehicleOperators.children];
        const operatorList = getSortedOperatorsList(operatorsToSort);
        const sortedOperatorList = [...operatorList].map((operator) => operator.value);

        _.set(endorsementVM, `lobData.personalUmbrella.coverables.vehicleOperators.value`, sortedOperatorList);

        return getSortedOperatorsList(operatorList);
    }, [pupModel,endorsementVM]);

    const vehicleOperatorsVM = useMemo(() => {
        return sortedVehicleOperatorsML.map((vehicleOperatorVM) => {
            vehicleOperatorVM.contact.updated.value = true;
            vehicleOperatorVM.isAgeFirstLicensedRequired.value = true;
            vehicleOperatorVM.isOccupationTypeRequired.value = true;
            vehicleOperatorVM.isEmailAddressRequired.value = vehicleOperatorVM.commPreference.value === 'electronic';

            if (isDriver(vehicleOperatorVM)) {
                vehicleOperatorVM.isLicenseNumberRequired.value = true;
                vehicleOperatorVM.isLicenseStateRequired.value = true;
            } else {
                vehicleOperatorVM.isLicenseNumberRequired.value = false;
                vehicleOperatorVM.isLicenseStateRequired.value = false;
            }

            if (vehicleOperatorVM.isPrimaryNamedInsured.value) {
                vehicleOperatorVM.isHasLossWithin5YearsRequired.value = true;
            } else {
                vehicleOperatorVM.isHasLossWithin5YearsRequired.value = false;
            }

            vehicleOperatorVM.value.hasEmailAddressInput = !!vehicleOperatorVM.emailAddress.value;
            vehicleOperatorVM.editMode_ML.value = true;
            return vehicleOperatorVM;
        });
    }, [sortedVehicleOperatorsML]);

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Additional Info to Purchase Screen'}, endorsementVM, authHeader)
    }, [])
    
    useEffect(() => {
        vehicleOperatorsVM.forEach((operator) => {
            operator.value.originalEmailAddress = _.get(operator, 'emailAddress.value');
        });
    }, []);

    const disableEmailAddress = (operator) => {
        return (!_.get(endorsementVM, 'canEditCommunicationsFields_Ext.value') && _.get(operator, 'value.originalEmailAddress'));
    };

    const watercraftOperatorsVM = useMemo(() => {
        return pupModel.coverables.watercraftOperators.children.map((watercraftOperatorVM) => {
            watercraftOperatorVM.isYearsOfExperienceRequired.value = true;
            return watercraftOperatorVM;
        });
    }, [pupModel]);

    const getVehicleName = (vehicleVM) => {
        const isVehiclePrivateOrClassic = () => {
            switch (vehicleVM.type.value.code) {
                case 'private_passenger_autos_ml':
                case 'classic_ml':
                    return true;
                default:
                    return false;
            }
        };

        if (isVehiclePrivateOrClassic()) {
            return `${vehicleVM.value.getYearMakeModelNameML(translator)} ${vehicleVM.trim_ML.value}`;
        }

        return vehicleVM.value.getYearMakeModelNameML(translator);
    };

    const isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal = (vehicleVM) => {
        switch (vehicleVM.type.value.code) {
            case 'trailer_ml':
            case 'GolfCart_ML':
            case 'LowSpeed_ML':
            case 'Other_Personally_Registered':
                return true;
            default:
                return false;
        }
    };

    const getPersonFullName = (operator) => {
        const contact = operator.contact.value;
        return contact.displayFullName_Ext;
    };

    const isEmailAddressVisible = (vehicleOperatorVM) => {
        let isVisible = false;
        if (vehicleOperatorVM.isPrimaryNamedInsured.value && vehicleOperatorVM.commPreference.value.code === 'electronic') {
            isVisible = true;
        } else if (vehicleOperatorVM.contact.value.address.state === 'CO' && endorsementVM.value.isPersonalAuto() && vehicleOperatorVM.contact.maritalStatus.value.code === 'M') {
            isVisible = true;
        } else if ((vehicleOperatorVM.contact.value.address.state === 'GA' || vehicleOperatorVM.contact.value.address.state === 'UT') && vehicleOperatorVM.driverType.value.code === 'excluded') {
            isVisible = true;
        }
        return isVisible;
    };

    const onLicenseNumberChange = () => {
        const allLicenseNumbers = watercraftOperatorsVM.map((operator) => operator.value.licenseNumber);
        const licenseNumbersCount = allLicenseNumbers.reduce((accumulator, currentValue) => {
            accumulator[currentValue] = accumulator[currentValue] || 0;
            accumulator[currentValue]++;

            return accumulator;
        }, {});

        let duplicate = false;
        Object.values(licenseNumbersCount).forEach((count) => {
            if (count > 1) {
                duplicate = true;
            }
        });

        setHasDuplicateLicenseNumber(duplicate);
    };

    const isVinDuplicated = useCallback((currentVehicle) => {
        const vehicles = _.get(endorsementVM, 'lobData.personalUmbrella.coverables.vehicles.children', []);
        const vehicleVin = currentVehicle.vin;
        const vehiclesWithTheSameVin = vehicles ? vehicles.filter(
            (x) => !_.isUndefined(x.vin.value) && x.vin.value === vehicleVin.value
        ) : [];
        return vehiclesWithTheSameVin.length >= 2;
    }, [endorsementVM]);


    const checkIfVinsVerified = () => {
        return !vehiclesVM.some((vehicle) => {
            return (!isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) && !vehicle.isValidVin);
        });
    };

    const checkForUniqueDriversLicenseNumbers = () => {
        clearBlockAlerts('duplicateLicense');

        const drivers = endorsementVM.lobData.personalUmbrella.coverables.vehicleOperators.value.filter((d) => {
            return d.licenseNumber && (d.driverType === 'rated' || d.driverType === 'excluded');
        });
        const seen = new Set();
        let hasDuplicate = false;

        drivers.forEach((d) => {
            if (seen.has(d.licenseNumber)) {
                hasDuplicate = true;
            } else {
                seen.add(d.licenseNumber);
            }
        });

        if(hasDuplicate) {
            addErrorAlert(messages.duplicateLicenseError, 'duplicateLicense');
            
            return false;
        }

        return true;
    };

    const isPageValid = () => {
        if (!endorsementVM.aspects.valid || !endorsementVM.aspects.subtreeValid) {
            return false;
        }

        // Check watercraft operators for unique license numbers
        if (hasDuplicateLicenseNumber) {
            return false;
        }

        // check rated and excluded drivers for unique license numbers
        if (!checkForUniqueDriversLicenseNumbers()) {
            return false;
        }

        return true;
    };

    const checkAllRequiredVinsLookedUp = () => {
        return vehiclesVM.every((vehicle) => {
            return (isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) || !_.isNil(vehicle.isValidVin));
        });
    };

    const saveAndQuote = () => {
        endorsementVM.value.portalCurrentStep_ML = currentStep.mlStepName;
        const promise = EndorsementService.saveAndQuote(endorsementVM.value, authHeader)
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            endorsementVM.value = new PolicyChange(responseData);
            MLTagManagerService.sendFormStepPolicyChangeStep();
            updateWizardData(wizardData);
            goNext();
        });
    }

    const verifyVinsOnLoad = () => {
        const intervalPromise = new Promise((resolve, reject) => {
            let repetitions = 12;
            const vinInterval = setInterval(() => {
                if (checkAllRequiredVinsLookedUp()) {
                    clearInterval(vinInterval);
                    if (checkIfVinsVerified()) {
                        resolve();
                    } else {
                        reject();
                    }
                }

                repetitions -= 1;
                if (repetitions === 0) {
                    clearInterval(vinInterval);
                    reject();
                }
            }, 250);
        });
        showProgressDialog(messages.verifyingVins, intervalPromise, true, 0);
        intervalPromise.then(() => {
            saveAndQuote();
        }, () => {
            setHidePage(false)
        });
    };

    const verifyAndSkipPage = () => {
        if (isPageValid()) {
            verifyVinsOnLoad();
            return;
        }
        setHidePage(false);
    };

    useEffect(() => {
        verifyAndSkipPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateInValidVin = (index) => {
        setInValidVin((prev) => {
            if (!prev.includes(index)) {
                return [...prev, index];
            }
           
            return prev;
        });
    }
     
      const removeInValidVin = (index) => {
        setInValidVin((prev) => prev.filter((invalid) => invalid !== index));
    }

    const onNext = () => {
        if (isPageValid() && checkIfVinsVerified() && isComponentValid) {
            saveAndQuote();
        } else {
            setShowErrors(true);
            setErrorTimestamp(errorTimestamp + 1);
        }
    };

    const getVehiclesOverrides = () => {
        const overrides = vehiclesVM.map((vehicleVM, index) => {
            return {
                [`vehicleName${index}`]: {
                    content: `${getVehicleName(vehicleVM)} ${translator(messages.vehicleX, { vehicleNumber: vehicleVM.vehicleNumber_ML.value })}`
                },
                [`vinContainer${index}`]: {
                    visible: !isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                },
                [`vinLookupComponent${index}`]: {
                    vehicleVM,
                    quoteID: endorsementVM.jobID,
                    path: `lobData.personalUmbrella.coverables.vehicles.children[${index}]`,
                    isVinDuplicated,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    updateIsValidVin: (vinValidFlag) => {
                        vehicleVM.isValidVin = vinValidFlag;
                    },
                    index,
                    onValidate,
                    showValidationErrors: showErrors,
                    updateVehicleDetails: refreshData,
                    isPUP: true,
                    isGpa: true,
                    updateInValidVin,
                    removeInValidVin
                },
                [`serialNumberContainer${index}`]: {
                    visible: isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                }
            }
        });

        return Object.assign({}, ...overrides);
    };

    const validateEmailAddress = (vehicleOperatorVM) => {
        setShowErrors(false);
        const account = vehicleOperatorVM;
        let emailAddress1 =  _.get(account, 'emailAddress.value');
        if(emailAddress1){
            emailAddress1 = emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                if(res === true){
                    _.set(account, 'emailVerified_Ext.value', true);
                    _.set(account, 'emailAddress.value', emailAddress1);
                }
                else{
                    _.set(account, 'emailVerified_Ext.value', false);
                    _.set(account, 'emailAddress.value', emailAddress1 + ' ');
                    setShowErrors(true);
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(messages.invalidEmail), 'invalidEmail');
                _.set(account, 'emailAddress.value', emailAddress1);
                _.set(account, 'emailVerified_Ext.value', false);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
            });
        }  
    };

    const emailTrim = ( vehicleOperatorVM, event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1;
        }
        
        let email =  _.get(vehicleOperatorVM, 'emailAddress.value')
        if(email === "" && email !== undefined){
            email = email.trim();
            _.set(vehicleOperatorVM, 'emailAddress.value', email);
        }
    }

    const getVehicleOperatorsOverrides = () => {
        const overrides = vehicleOperatorsVM.map((vehicleOperatorVM, index) => {
            return {
                [`vehicleOperatorName${index}`]: {
                    content: `${getPersonFullName(vehicleOperatorVM)} ${!isExcludedDriver(vehicleOperatorVM) ? translator(messages.driverX, { driverNumber: index + 1 }) : ''} ${isExcludedDriver(vehicleOperatorVM) ? translator(messages.excludedDriverX, { driverNumber: index + 1 }) : ''}`
                },
                [`emailContainer${index}`]: {
                    visible: isEmailAddressVisible(vehicleOperatorVM),
                    onBlur: () => {
                        validateEmailAddress(vehicleOperatorVM)
                    },
                    onFocus: (event) => emailTrim(vehicleOperatorVM, event),
                    showValidationErrors: showErrors,
                },
                [`email${index}`]: {
                    disabled: disableEmailAddress(vehicleOperatorVM)
                },
                [`licenseNumberContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`licenseStateContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`ageFirstLicensedContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`isPrimaryNamedInsuredContainer${index}`]: {
                    visible: vehicleOperatorVM.isPrimaryNamedInsured.value === true
                }
            }
        });

        return Object.assign({}, ...overrides);
    }

    const getWatercraftOperatorsOverrides = () => {
        const overrides = watercraftOperatorsVM.map((watercraftOperatorVM, index) => {
            return {
                [`watercraftOperatorName${index}`]: {
                    content: `${getPersonFullName(watercraftOperatorVM)} ${translator(messages.watercraftOperatorX, { watercraftOperatorNumber: index + 1 })}`
                },
                [`watercraftLicenseNumber${index}`]: {
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                        onLicenseNumberChange();
                    }
                }
            }
        });

        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        pageContainer: {
            className: hidePage ? styles.displayNone : ''
        },
        duplicateLicenseNumberError: {
            visible: hasDuplicateLicenseNumber
        },
        vehiclesIterable: {
            data: vehiclesVM
        },
        vehicleOperatorsIterable: {
            data: vehicleOperatorsVM
        },
        vehicleOperatorsIterable2: {
            data: vehicleOperatorsVM
        },
        watercraftOperatorsIterable: {
            data: watercraftOperatorsVM
        },
        nextDescription: {
            content: (
                <FormattedMessage
                    {...messages.nextDesc}
                    values={{
                        reviewAdditionalActivity: <strong>{translator(messages.reviewAdditionalActivity)}</strong>,
                        mvrsMayBeRun: <strong>{translator(messages.mvrsMayBeRun)}</strong>
                    }}
                />
            )
        },
        stickyContinueSection: {
            defaultContinueSectionId: 'footer',
            nextDescription: (
                <FormattedMessage
                    {...messages.nextDesc}
                    values={{
                        reviewAdditionalActivity: <strong>{translator(messages.reviewAdditionalActivity)}</strong>,
                        mvrsMayBeRun: <strong>{translator(messages.mvrsMayBeRun)}</strong>
                    }}
                />
            ),
            onNext,
            disableNext:inValidVin?.length > 0
        },
        nextButton:{disabled:inValidVin?.length > 0},
        ...getVehiclesOverrides(),
        ...getVehicleOperatorsOverrides(),
        ...getWatercraftOperatorsOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    }

    return (
        <WizardPage
            policyChangePageHeaderML={messages.additionalDetailsHeader}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                model={endorsementVM}
                onModelChange={refreshData}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

AdditionalInfoToPurchasePagePolChange.propTypes = wizardProps;
export default AdditionalInfoToPurchasePagePolChange;